import styled from 'styled-components';
import {
  Drawer,
  List,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';

import colors from '../../../../styles/colors';

const StyledTitle = styled(Typography)`
  margin-left: 10px;
`;

const openedMixin = (theme) => ({
  width: theme.drawer.width,
  background: colors.base.secondary2,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  borderRight: 'none',
  color: colors.shades.dark4,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: colors.base.secondary2,
  // closedMixin used to set no colour, so the collapsed rail inherited text.primary
  // (ink) from ScopedCssBaseline — ink on the ink rail. Harmless while the labels are
  // opacity:0, but it left a 1:1 contrast trap one style change away from being visible.
  color: colors.shades.dark4,
  overflowX: 'hidden',
  borderRight: 'none',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: theme.drawer.width,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

/**
 * The rail sits on ink, so its selected/hover states cannot use the theme's
 * action.selected (a light mint tuned for white surfaces) — that would put lime
 * text on a near-white background. Mirrors the design's translucent lime instead,
 * keeping the active item legible against ink.
 */
const ListItemContainer = styled.div`
  margin: 5px;

  .MuiListItemIcon-root {
    color: ${colors.shades.dark4};
  }

  .MuiDivider-root {
    border-color: ${colors.addAlpha(colors.base.alpha, 0.12)};
  }

  .MuiListItem-root {
    border-radius: 8px;

    &:hover {
      background: ${colors.addAlpha(colors.base.primary, 0.08)};
    }

    &.Mui-selected {
      background: ${colors.addAlpha(colors.base.primary, 0.16)};
      color: ${colors.base.primary};

      &:hover {
        background: ${colors.addAlpha(colors.base.primary, 0.22)};
      }

      .MuiListItemIcon-root {
        color: ${colors.base.primary};
      }
    }
  }
`;

/**
 * While open, the list is laid out at the full drawer width instead of following the
 * paper's animated width, so wrapping labels break once at their final width rather
 * than changing row heights on every frame of the opening transition. The paper's
 * overflowX: hidden clips it while the drawer widens.
 *
 * The list scrolls itself, not the paper: a classic scrollbar then sits inside the
 * list's own width, so it narrows the items instead of covering their right edge.
 */
const StyledList = styled(List).withConfig({
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  ...(open && { width: theme.drawer.width }),
}));

const StyledListItemText = styled(ListItemText).withConfig({
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  opacity: open ? 1 : 0,
  // Hidden labels in the collapsed rail would otherwise wrap and stretch its rows.
  whiteSpace: open ? 'normal' : 'nowrap',
  overflow: 'hidden',
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
    delay: open ? theme.transitions.duration.enteringScreen : 0,
  }),
}));

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  color: ${colors.base.primary};
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
  color: ${colors.base.primary};
`;

export {
  StyledDrawer,
  StyledTitle,
  DrawerHeader,
  ListItemContainer,
  StyledList,
  StyledListItemText,
  StyledChevronLeftIcon,
  StyledChevronRightIcon,
};
